// useDialog.tsx
import { useState, ReactNode, useCallback, JSX } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';

export interface DialogButton<T = any> {
  label: string;
  value: T;
  color?: 'primary' | 'secondary' | 'error' | 'success' | 'warning';
  autoFocus?: boolean;
  disabled?: boolean;
}

export interface DialogOptions<T = any> {
  title?: string;
  message?: ReactNode;
  buttons?: DialogButton<T>[];
  input?: boolean; // для prompt
  defaultValue?: string; // начальное значение для input
}

interface UseDialogReturn<T = any> {
  showDialog: (options: DialogOptions<T>) => Promise<T | string | null>;
  DialogComponent: JSX.Element;
}

export function useDialog<T = any>(): UseDialogReturn<T> {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<DialogOptions<T>>({});
  const [resolver, setResolver] = useState<((value: T | string | null) => void) | null>(null);
  const [inputValue, setInputValue] = useState<string>('');

  const showDialog = useCallback((opts: DialogOptions<T>) => {
    return new Promise<T | string | null>((resolve) => {
      setOptions(opts);
      setInputValue(opts.defaultValue || '');
      setResolver(() => resolve);
      setOpen(true);
    });
  }, []);

  const handleClose = (value: T | string | null) => {
    setOpen(false);
    resolver?.(value);
  };

  const handleButtonClick = (btn: DialogButton<T>) => {
    if (options.input) {
      handleClose(inputValue); // игнорируем btn.value, возвращаем введённое
    } else {
      handleClose(btn.value);
    }
  };

  const DialogComponent = (
    <Dialog
      open={open}
      onClose={() => handleClose(null)}
      aria-labelledby="dialog-title"
    >
      {options.title && <DialogTitle id="dialog-title">{options.title}</DialogTitle>}
      {options.message && <DialogContent>{options.message}</DialogContent>}
      {options.input && (
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            variant="outlined"
            margin="dense"
          />
        </DialogContent>
      )}
      <DialogActions>
        {options.buttons?.map((btn, idx) => (
          <Button
            key={idx}
            onClick={() => handleButtonClick(btn)}
            color={btn.color || 'primary'}
            autoFocus={btn.autoFocus}
            disabled={btn.disabled}
          >
            {btn.label}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );

  return { showDialog, DialogComponent };
}
